// JSON source text access (Chrome 114, Firefox 135, Safari 18.4). TypeScript's lib doesn't declare it yet.
declare global {
  interface RawJSON {
    readonly rawJSON: string;
  }
  interface JSON {
    rawJSON?(text: string): RawJSON;
  }
}

/**
 * A JSON number whose text a JavaScript number can't reproduce, such as `1.0`
 * or an id past 2^53. Code that treats it as a number gets `Number(text)`, but
 * `typeof` reads `object`, so a `typeof value === 'number'` check skips it.
 */
export class ExactNumber {
  readonly #text: string;

  constructor(text: string) {
    this.#text = text;
  }

  toString(): string {
    return this.#text;
  }

  valueOf(): number {
    return Number(this.#text);
  }

  toJSON(): number {
    return this.valueOf();
  }
}

/** A JSON number's text as a plain number, or as an `ExactNumber` when a plain number would change it. */
export function parseExactNumber(text: string): number | ExactNumber {
  const number = Number(text);
  return String(number) === text ? number : new ExactNumber(text);
}

/**
 * `JSON.parse` reviver that keeps each number's original text. A browser
 * without source text access passes no `context`, so numbers stay plain.
 */
export function reviveExactNumber(_key: string, value: unknown, context?: { source?: string }) {
  return typeof value === 'number' && context?.source != null ? parseExactNumber(context.source) : value;
}

/** `JSON.stringify`, except an `ExactNumber` keeps its original text. */
export function stringifyJsonExactly(value: unknown, space?: number): string {
  // The replacer sees `toJSON`'s plain number, so read the original from the holder.
  return JSON.stringify(
    value,
    function (key: string, child: unknown) {
      const original: unknown = this[key];
      return original instanceof ExactNumber ? (JSON.rawJSON?.(String(original)) ?? child) : child;
    },
    space,
  );
}

export const MIN_UNIX_TIMESTAMP = 99999999;
export const MAX_UNIX_TIMESTAMP = 10000000000;

export function isValidTimestamp(value: unknown): boolean {
  const text = String(value);
  if (/\D/.test(text)) return false;
  const number = Number(text);
  return Number.isInteger(number) && number > MIN_UNIX_TIMESTAMP && number < MAX_UNIX_TIMESTAMP;
}

export function isUrl(value: string): boolean {
  return /^(http|https):\/\/[^\s]+$/.test(value);
}
